import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
	Box,
	Button,
	Card,
	CardContent,
	Divider,
	Skeleton,
	Stack,
	Typography,
} from "@mui/material";
import {
	useArrangement,
	useArrangementList,
} from "../../hooks/useArrangements";
import { useGetActiveRolesByAccountId } from "../../hooks/useRoles";
import {
	ArrangementSurface,
	AuthorView,
	SURFACES,
} from "../../services/arrangements.service";
import { countsOf } from "../../pages/Arrangements/editor";
import {
	AccountRole,
	ListedArrangement,
	uncoveredRoles,
} from "../../pages/Arrangements/roles";
import { SURFACE_LABEL } from "../../pages/Arrangements/words";

/**
 * SIMS-8555 8f — "Dashboard & statistics arrangements" on a program's
 * configuration: one row per surface, its summary line, and Arrange.
 *
 * Arranging is its own record, so this section stays open after participants
 * enroll, when the program's own fields lock (AC10). The line counts the entries
 * no longer available (AC8). One arrangement per surface here; the counts of
 * role-restricted arrangements arrive with SIMS-8562.
 */

const plural = (n: number, one: string, many: string): string =>
	`${n} ${n === 1 ? one : many}`;

export const summaryLine = (view: AuthorView): string => {
	if (!view.arranged) {
		return "Not yet arranged — the default presentation of each measurement is shown";
	}
	const c = countsOf(view);
	const parts = [
		"Arranged",
		plural(c.sections, "section", "sections"),
		plural(c.measurements, "measurement", "measurements"),
	];
	if (c.unavailable > 0) {
		parts.push(`${c.unavailable} no longer available`);
	}
	return parts.join(" · ");
};

/**
 * SIMS-8562 (Q-5): the surface's arrangements counted, e.g. "3 arrangements · 2 role
 * restricted · 1 default · 2 roles see the default". A role sees "the default" when
 * no arrangement is aimed at it: the unrestricted arrangement, or the default view.
 */
export const listLine = (
	list: readonly ListedArrangement[],
	roles: readonly AccountRole[],
): string => {
	const restricted = list.filter((a) => a.roleIds.length > 0).length;
	const unaimed = uncoveredRoles(list, roles).length;
	return [
		plural(list.length, "arrangement", "arrangements"),
		`${restricted} role restricted`,
		`${list.length - restricted} default`,
		`${plural(unaimed, "role sees", "roles see")} the default`,
	].join(" · ");
};

const SurfaceRow: React.FC<{
	programId: string;
	surface: ArrangementSurface;
	onView?: (surface: ArrangementSurface, view: AuthorView) => void;
}> = ({ programId, surface, onView }) => {
	const query = useArrangement(programId, surface);
	const list = useArrangementList(programId, surface);
	const { data: accountRoles } = useGetActiveRolesByAccountId();
	const arrangements = list.data?.arrangements ?? [];
	const onlyRestricted =
		arrangements.length > 0 && arrangements.every((a) => a.roleIds.length > 0);
	React.useEffect(() => {
		if (query.data && onView) onView(surface, query.data);
	}, [query.data, onView, surface]);

	let line: React.ReactNode;
	if (query.isPending) line = <Skeleton width={320} />;
	else if (query.isError) {
		const status = (query.error as { statusCode?: number } | null)?.statusCode;
		line =
			status === 403 || status === 404
				? "Not available to you for this program."
				: "Couldn't be loaded.";
	} else if (onlyRestricted) {
		// Every arrangement is aimed at roles: there is no "the" arrangement to describe.
		line =
			"Every arrangement is role restricted; other roles see the default view.";
	} else if (query.data) line = summaryLine(query.data);

	return (
		<Stack
			direction="row"
			alignItems="center"
			spacing={2}
			sx={{ py: 1.5 }}
			data-testid={`program-arrangements-${surface}`}
		>
			<Box sx={{ flexGrow: 1 }}>
				<Typography variant="subtitle1" fontWeight={600}>
					{SURFACE_LABEL[surface]}
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					data-testid={`program-arrangements-${surface}-line`}
				>
					{line}
				</Typography>
				{arrangements.length > 0 && (
					<Typography
						variant="body2"
						color="text.secondary"
						data-testid={`program-arrangements-${surface}-list`}
					>
						{listLine(
							arrangements,
							(accountRoles ?? []).map((r) => ({ id: r.id, name: r.name })),
						)}
					</Typography>
				)}
			</Box>
			{query.data ? (
				<Button
					variant="outlined"
					component={RouterLink}
					to={`/programs/${encodeURIComponent(programId)}/arrangements?surface=${surface}`}
					data-testid={`program-arrangements-${surface}-arrange`}
				>
					Arrange
				</Button>
			) : (
				// Not a link until the arrangement has loaded: nothing to follow while disabled.
				<Button
					variant="outlined"
					disabled
					data-testid={`program-arrangements-${surface}-arrange`}
				>
					Arrange
				</Button>
			)}
		</Stack>
	);
};

const ArrangementsSummary: React.FC<{ programId: string }> = ({
	programId,
}) => {
	// The footer counts what the program declares: the dashboard's latest view, or
	// the statistics view's until the dashboard's arrives (or if it is refused).
	const [views, setViews] = React.useState<
		Partial<Record<ArrangementSurface, AuthorView>>
	>({});
	const onView = React.useCallback(
		(surface: ArrangementSurface, view: AuthorView) =>
			setViews((v) => ({ ...v, [surface]: view })),
		[],
	);
	const declared = views.PROGRAM_DASHBOARD ?? views.PARTICIPANT_STATISTICS;
	const counts = declared ? countsOf(declared) : null;
	return (
		<Card variant="outlined" data-testid="program-arrangements">
			<CardContent>
				<Typography variant="h6" component="h3" gutterBottom>
					Dashboard &amp; statistics arrangements
				</Typography>
				{SURFACES.map((surface, i) => (
					<React.Fragment key={surface}>
						{i > 0 && <Divider />}
						<SurfaceRow
							programId={programId}
							surface={surface}
							onView={onView}
						/>
					</React.Fragment>
				))}
				{counts && (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mt: 1 }}
						data-testid="program-arrangements-declared"
					>
						{plural(counts.declared, "measurement", "measurements")} declared
						across {plural(counts.events, "event", "events")}
					</Typography>
				)}
			</CardContent>
		</Card>
	);
};

export default ArrangementsSummary;
